import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import TextArea from "../../components/Inputs/TextAreaInput";
import {
  createIndustryCollaboration,
  deleteIndustryCollaboration,
  getIndustryCollaboration,
  getIndustryCollaborations,
  updateIndustryCollaboration,
} from "../../services/industryCollaborationService";
import { industryCollaborationSchema } from "../../validators/industryCollaborationValidators";
import DateInput from "../../components/Inputs/DateInput";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const IndustryCollaboration = () => {
  const [collaborations, setCollaborations] = useState([]);
  const [filteredCollaborations, setFilteredCollaborations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [collaborationToEdit, setCollaborationToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [collaborationToDelete, setCollaborationToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadCollaborations();
    loadInstitutions();
  }, []);

  useEffect(() => {
    let filtered = collaborations;

    if (searchTerm.trim() !== "") {
      filtered = collaborations.filter((item) =>
        `${item.partner_name} ${item.institution.name} ${item.partner_industry} ${item.project}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredCollaborations(filtered);
  }, [searchTerm, collaborations, selectedInstitution]);

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadCollaborations = async () => {
    setLoading(true);
    try {
      const { data } = await getIndustryCollaborations();

      setCollaborations(data.data);
      setFilteredCollaborations(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load collaborations"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (collaborationToEdit) {
      onUpdateCollaboration(payload, resetForm);
    } else {
      onCreateCollaboration(payload, resetForm);
    }
  };

  const onCreateCollaboration = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createIndustryCollaboration(payload);

      setCollaborations((prev) => [...prev, data.data]);

      toast.success("Collaboration added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Collaboration was not added, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setCollaborationToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateCollaboration = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalCollaborations = [...collaborations];

      const collaborationIndex = originalCollaborations.findIndex(
        (collaboration) => collaboration.id === collaborationToEdit.id
      );

      if (collaborationIndex === -1)
        throw new Error("Collaborations not found.");

      const { data } = await updateIndustryCollaboration(
        collaborationToEdit.id,
        payload
      );

      originalCollaborations[collaborationIndex] = data.data;

      setCollaborations(originalCollaborations);

      setCollaborationToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Collaboration updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Collaboration not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setCollaborationToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteCollaboration = async () => {
    const originalCollaborations = [...collaborations];

    const newCollaborations = originalCollaborations.filter(
      (collaboration) => collaboration.id !== collaborationToDelete.id
    );

    setCollaborations(newCollaborations);

    try {
      await deleteIndustryCollaboration(collaborationToDelete?.id);

      toast.success("Collaboration deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Collaboration not deleted, try again later"
        }`
      );

      setCollaborations(originalCollaborations);
    } finally {
      setIsDeleteModalOpen(false);

      setCollaborationToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setCollaborationToEdit(null);
    setCollaborationToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },

    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Partner",
      dataIndex: "partner_name",
      key: "partner_name",
    },
    {
      title: "Industry",
      dataIndex: "partner_industry",
      key: "partner_industry",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },
    {
      title: "Starting Date",
      dataIndex: "date_started",
      key: "date_started",
    },

    {
      title: "End Date",
      dataIndex: "date_ended",
      key: "date_ended",
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Collaboration">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Collaboration">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Collaborations" subtitle1="Collaborations" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Collaborations"
            count={filteredCollaborations.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Collaboration"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredCollaborations.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              !isInstAdmin && (
                <div className="mb-2 py-2 flex gap-2">
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                </div>
              )
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {collaborationToEdit
                    ? "Edit Collaboration"
                    : "Add Collaboration"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                partner_name: collaborationToEdit
                  ? collaborationToEdit?.partner_name
                  : "",
                partner_industry: collaborationToEdit
                  ? collaborationToEdit?.partner_industry
                  : "",
                project: collaborationToEdit
                  ? collaborationToEdit?.project
                  : "",
                description: collaborationToEdit
                  ? collaborationToEdit?.description
                  : "",

                institution_id: collaborationToEdit
                  ? collaborationToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",

                date_started: collaborationToEdit
                  ? collaborationToEdit?.date_started
                  : "",
                date_ended: collaborationToEdit
                  ? collaborationToEdit?.date_ended
                  : "",
              }}
              enableReinitialize
              validationSchema={industryCollaborationSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          collaborationToEdit
                            ? collaborationToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <TextInput
                      name="partner_name"
                      title="Partner name"
                      placeholder="e.g. MTN Uganda"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextInput
                      name="partner_industry"
                      title="Partner Industry"
                      placeholder="e.g. Telecommunications"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextInput
                      name="project"
                      title="Project"
                      placeholder="e.g. Rural Connectivity Pilot Project"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextArea
                      name="description"
                      title="Description"
                      placeholder="e.g. Joint research initiative to develop and deploy low-cost solar kits for rural communities."
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <DateInput
                      name="date_started"
                      title="Starting Date"
                      placeholder="Select Starting date"
                      errors={errors}
                      values={values}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />

                    <DateInput
                      name="date_ended"
                      title="End Date"
                      placeholder="Select End date"
                      errors={errors}
                      values={values}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteCollaboration}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default IndustryCollaboration;
